import {concat, noop, without} from 'lodash';
import React, {ReactNode, RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {globalWindow} from '../../../../core/src/helpers/browser/browserHelpers';
import {getWindow} from '../../../../core/src/helpers/browser/domHelpers';
import {usePrevious} from '../../../../core/src/helpers/react/hooks/hookHelpers';
import {useUniqueElementId} from '../../../../core/src/helpers/react/hooks/uniqueIdHelpers';
import {isDefined, isNotNull} from '../../../../core/src/helpers/typescript/typeHelpers';
import {useMouseCursorContext} from '../mouseCursor/mouseCursorContext';
import {TooltipCondition, TooltipContextProps} from './tooltipContext';
import {TooltipCoordinatorTooltipRenderer} from './tooltipCoordinatorTooltipRenderer';
import {UseTooltipCoordinatorOptions} from './tooltipCoordinatorTypes';
import {tooltipDefaultDelay} from './tooltipHelpers';
import {useTooltipOrchestrator} from './tooltipOrchestratorContext';

interface UseTooltipCoordinatorResult<T extends HTMLElement> {
  anchorRef: RefObject<T>;
  contextValue: TooltipContextProps;
  handlers: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onMouseDown: () => void;
  };
  tooltipElement: ReactNode;
  isVisible: boolean;
  hide: () => void;
}

/**
 * Hook that provides tooltip coordination without a wrapper DOM element.
 *
 * The returned `contextValue` should be passed to a `TooltipContext.Provider` wrapping the anchor
 * to let children control the tooltip's display conditions and react to its visibility.
 */
export function useTooltipCoordinator<T extends HTMLElement = HTMLElement>(
  options: UseTooltipCoordinatorOptions,
): UseTooltipCoordinatorResult<T> {
  const {render, plainTooltip, placement, onShow, onHide} = options;
  const anchorRef = useRef<T>(null);
  const conditionsRef = useRef<ReadonlyArray<TooltipCondition>>([]);
  const mockRenderProps = useMemo(() => ({anchor: anchorRef, onRequestTooltipClose: () => {}}), []);

  const hasContent = useMemo(() => {
    // Ensure the render of the tooltip is _actually_ not null because otherwise assumptions in `useTooltipVisibility` are subtly wrong.
    const tooltip = plainTooltip || (render && render(mockRenderProps));
    return isDefined(tooltip) && isNotNull(tooltip);
  }, [mockRenderProps, plainTooltip, render]);

  const {isVisible, hide, handlers} = useTooltipVisibility({
    anchorRef,
    conditionsRef,
    showDelay: options.delay,
    hideDelay: options.persistent ? 300 : 0,
    hideOnMouseDown: options.hideOnMouseDown,
    hasContent,
    isAlwaysVisibleDevOverride: options.isAlwaysVisible,
  });

  useNotifyListeners(isVisible, onShow, onHide);

  // Optimization: Start tracking cursor position, so we know where the cursor is before the tooltip is mounted.
  useMouseCursorContext(placement === 'cursor');

  const tooltipElement = (
    <TooltipCoordinatorTooltipRenderer
      render={render}
      plainTooltip={plainTooltip}
      onRender={options.onRender}
      placement={placement}
      isMultiline={options.isMultiline}
      customMargin={options.customMargin}
      maxWidth={options.maxWidth}
      minWidth={options.minWidth}
      preventOverflowPadding={options.preventOverflowPadding}
      persistent={options.persistent}
      tooltipColor={options.tooltipColor}
      borderRadius={options.borderRadius}
      isBoxShadowDisabled={options.isBoxShadowDisabled}
      tooltipZIndex={options.tooltipZIndex}
      shouldRemoveWrapper={options.shouldRemoveWrapper}
      isVisible={isVisible}
      hide={hide}
      anchorRef={anchorRef}
    />
  );

  const contextValue = useMemo<TooltipContextProps>(
    () => ({
      registerCondition: (condition) => {
        conditionsRef.current = concat(conditionsRef.current, condition);
        return () => {
          conditionsRef.current = without(conditionsRef.current, condition);
        };
      },
      isTooltipVisible: isVisible,
    }),
    [conditionsRef, isVisible],
  );

  return {
    anchorRef,
    contextValue,
    handlers,
    tooltipElement,
    isVisible,
    hide,
  };
}

function useNotifyListeners(isVisible: boolean, onShow?: () => void, onHide?: () => void) {
  const prevIsVisible = usePrevious(isVisible);
  if (!prevIsVisible && isVisible) {
    onShow?.();
    return;
  }

  if (prevIsVisible && !isVisible) {
    onHide?.();
  }
}

interface UseTooltipVisibilityOptions {
  anchorRef: RefObject<HTMLElement>;
  conditionsRef: RefObject<ReadonlyArray<TooltipCondition>>;
  showDelay: number | undefined;
  hideDelay: number | undefined;
  hideOnMouseDown: boolean | undefined;
  hasContent: boolean;
  isAlwaysVisibleDevOverride: boolean | undefined;
}

function useTooltipVisibility({
  anchorRef,
  conditionsRef,
  showDelay,
  hideDelay,
  hideOnMouseDown,
  hasContent,
  isAlwaysVisibleDevOverride,
}: UseTooltipVisibilityOptions) {
  const [isVisible, setIsVisible] = useState(false);
  const syncTimeoutRef = useRef(0);
  const baseTooltipId = useUniqueElementId();
  const {onTooltipHide, onTooltipShow, canShowTooltip} = useTooltipOrchestrator();

  const cancelSync = useCallback(() => {
    globalWindow.clearTimeout(syncTimeoutRef.current);
    syncTimeoutRef.current = 0;
  }, []);

  const immediatelySyncState = useCallback(
    (isEnabled: boolean) => {
      cancelSync();
      if (isEnabled && !canShowTooltip()) {
        return;
      }
      if (isEnabled) {
        setIsVisible(isEnabled);
        onTooltipShow(baseTooltipId, anchorRef);
      } else {
        setIsVisible(isEnabled);
        onTooltipHide(baseTooltipId);
      }
    },
    [anchorRef, baseTooltipId, canShowTooltip, cancelSync, onTooltipHide, onTooltipShow],
  );

  const scheduleSync = useCallback(
    (isEnabled: boolean, timeout: number) => {
      cancelSync();

      if (!timeout) {
        immediatelySyncState(isEnabled);
        return;
      }

      syncTimeoutRef.current = globalWindow.setInstrumentedTimeout(
        () => immediatelySyncState(isEnabled),
        timeout,
      );
    },
    [immediatelySyncState, cancelSync],
  );

  const onMouseEnter = useCallback(() => {
    if (!hasContent || hasAllUnsatisfiedConditions(conditionsRef)) {
      return;
    }

    scheduleSync(true, showDelay ?? tooltipDefaultDelay);
  }, [hasContent, conditionsRef, scheduleSync, showDelay]);

  const onMouseLeave = useCallback(() => {
    scheduleSync(false, hideDelay ?? 0);
  }, [hideDelay, scheduleSync]);

  const onMouseDown = useCallback(() => {
    if (!hideOnMouseDown) {
      return;
    }

    immediatelySyncState(false);
  }, [hideOnMouseDown, immediatelySyncState]);

  useEffect(() => {
    const window = getWindow(anchorRef.current);
    if (!window) {
      return noop;
    }

    const onWindowMouseLeave = () => {
      immediatelySyncState(false);
    };

    window.addEventListener('mouseleave', onWindowMouseLeave);
    return () => {
      window.removeEventListener('mouseleave', onWindowMouseLeave);
    };
  }, [anchorRef, immediatelySyncState]);

  return {
    isVisible: isAlwaysVisibleDevOverride || isVisible,
    hide: useCallback(() => immediatelySyncState(false), [immediatelySyncState]),
    handlers: {
      onMouseEnter,
      onMouseLeave,
      onMouseDown,
    },
  };
}

function hasAllUnsatisfiedConditions(conditionsRef: RefObject<ReadonlyArray<TooltipCondition>>) {
  if (!conditionsRef.current || !conditionsRef.current.length) {
    return false;
  }

  return conditionsRef.current.every((condition) => !condition());
}
